// ProducerContent.js
import React, { useState, useEffect } from 'react';
import { List, Typography, Row, Col, Button, message } from 'antd';
import useGraphqlService from "../../../util/useGraphqlService";
import {GET_EPISODE_QUERY} from "../../../constants/graphql/episodeQueries";
import BarcodeScannerModal from "../../BarcodeScannerModal";
import EntryDetailsModal from "./EntryDetailsModal";
import {GET_ENTRY_QUERY, UPDATE_ENTRY_MUTATION} from "../../../constants/graphql/entryQueries";
import moment from "moment";
import BucketPullDetails from "./BucketPullDetails";
import Stopwatch from "./Stopwatch";
import BroadcastBucketPullButton from "./BroadcastBucketPullButton";
import StyledListItem from "../../styled/StyledListItem";

const BucketPullsContent = ({ episodeId }) => {
    const { sendGraphqlRequest } = useGraphqlService();
    const [bucketPulls, setBucketPulls] = useState([]);
    const [isScannerModalVisible, setScannerModalVisible] = useState(false);
    const [isDetailsModalVisible, setDetailsModalVisible] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [selectedBucketPull, setSelectedBucketPull] = useState(null);

    useEffect(() => {
        if (episodeId) {
            fetchEpisodeData();
        }
    }, [episodeId]);

    const fetchEpisodeData = async () => {
        try {
            const data = await sendGraphqlRequest(GET_EPISODE_QUERY, { id: episodeId });
            setBucketPulls(data?.episode?.bucketPulls);
        } catch (error) {
            console.error('Failed to fetch episode details:', error);
        }
    };

    const handleBarcodeScanned = async (uuid) => {
        setScannerModalVisible(false);
        try {
            if (!uuid) {
                return;
            }
            const data = await sendGraphqlRequest(GET_ENTRY_QUERY, { paperUuid: uuid });
            if (!data?.entry?.id) {
                message.error("Barcode not registered. Might be an imposter. ..or try again...maybe not.");
            } else {
                if(!data?.entry?.pulledAt) {
                    setSelectedEntry(data.entry);
                    setDetailsModalVisible(true);
                } else {
                    message.warning("This one was already pulled....drink less.");
                }
            }
        } catch (error) {
            console.error('Failed to fetch entry details:', error);
        }
    };

    const handleBucketPullSelect = (item) => {
        setSelectedBucketPull(prevSelected =>
            prevSelected && prevSelected.id === item.id ? null : { type: 'bucketPull', ...item }
        );
    };

    const logTime = async (duration) => {
        try {
            await sendGraphqlRequest(UPDATE_ENTRY_MUTATION, { input: { id: selectedBucketPull?.id, duration } });

            // Update the selectedBucketPull
            setSelectedBucketPull((prev) => ({ ...prev, duration }));

            // Update the bucketPulls collection
            setBucketPulls((prevBucketPulls) =>
                prevBucketPulls.map((pull) =>
                    pull.id === selectedBucketPull?.id
                        ? { ...pull, duration }
                        : pull
                )
            );

            message.success('Time logged to bucket pull!');
        } catch (error) {
            console.error('Error logging time:', error);
            message.error('Failed to log time. Please try again.');
        }
    };

    return (
        <Row gutter={16}>
            <Col span={6}>
                <Button type="primary" style={{ width: '100%', position: 'sticky', top: 0, zIndex: 1000 }}
                        onClick={() => setScannerModalVisible(true)}>
                    Scan Bucket Pull Barcode
                </Button>
                <List
                    itemLayout="horizontal"
                    dataSource={bucketPulls}
                    renderItem={item => (
                        <StyledListItem
                            key={item.id}
                            onClick={() => handleBucketPullSelect(item)}
                            style={{
                                backgroundColor: selectedBucketPull && selectedBucketPull.id === item.id ? 'red' : 'black'
                            }}
                        >
                            <List.Item.Meta
                                title={<Typography.Text>{item.paperName}</Typography.Text>}
                                description={`Pulled at: ${moment(item.pulledAt).format('h:mm:ss a')}`}
                            />
                        </StyledListItem>
                    )}
                />
            </Col>
            <Col span={18}>
                <BroadcastBucketPullButton selectedBucketPull={selectedBucketPull} />
                {selectedBucketPull?.id && <Stopwatch onLogTime={logTime} />}
                {selectedBucketPull?.id ?
                    <BucketPullDetails bucketPull={selectedBucketPull} />
                    :
                    <div>Select a Bucket Pull</div>
                }
            </Col>
            <BarcodeScannerModal
                isVisible={isScannerModalVisible}
                onClose={(scannedResult) => handleBarcodeScanned(scannedResult)}
            />
            <EntryDetailsModal
                isVisible={isDetailsModalVisible}
                entryDetails={selectedEntry}
                onClose={() => setDetailsModalVisible(false)}
                onConfirm={() => {
                    setDetailsModalVisible(false);
                    fetchEpisodeData(); // Refresh the list after confirming
                }}
            />
        </Row>
    );
};

export default BucketPullsContent;
